import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/use-auth-compat';
import {
  createPurchaseOrder,
  verifyPurchasePayment,
  type PurchaseCurrency,
  type PurchaseOrderStatus,
} from '@/services/coinPurchaseService';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface PurchaseCoinsArgs {
  packageCode: string;
  currency: PurchaseCurrency;
  buyerRole: 'STUDENT' | 'TEACHER';
}

/**
 * Orchestrates a coin top-up: create the order server-side (authoritative
 * amount), open Razorpay's hosted checkout, then verify the payment
 * signature server-side before crediting coins. The backend's webhook is the
 * durable fallback if this client-side verify call never happens (closed tab
 * right after paying) — this hook only covers the fast-path UX.
 *
 * onRefresh — called after a successful purchase to reload the caller's coin
 * balance. Pass the refreshProfile function from whichever profile context
 * the page belongs to (StudentProfileContext or TeacherProfileContext).
 */
export function usePurchaseCoins(onRefresh?: () => Promise<void>) {
  const auth = useAuth();

  return useMutation<PurchaseOrderStatus, Error, PurchaseCoinsArgs>({
    mutationFn: async ({ packageCode, currency, buyerRole }) => {
      if (typeof window.Razorpay !== 'function') {
        throw new Error('Payment checkout failed to load. Please refresh and try again.');
      }

      const token = await auth.getToken();
      const order = await createPurchaseOrder(token, packageCode, currency, buyerRole);

      // Prefill the student's email so they don't have to re-type it in the
      // checkout overlay — small friction reducer, and Razorpay uses it for
      // the payment receipt email on their side too.
      const userEmail = auth.user?.email ?? auth.user?.profile?.email ?? undefined;

      // Role-specific accent color mirrors the app theme:
      //   student → teal  hsl(165 50% 40%) = #33997f
      //   teacher → navy  hsl(210 60% 25%) = #1a4066
      const themeColor = buyerRole === 'TEACHER' ? '#1a4066' : '#33997f';

      return new Promise<PurchaseOrderStatus>((resolve, reject) => {
        const razorpay = new window.Razorpay({
          key: order.keyId,
          amount: order.amount,
          currency: order.currency,
          order_id: order.razorpayOrderId,
          name: 'BookMyCoaching',
          description: `${order.coins} BMC Coins`,
          // Square 256×256 logo — Razorpay's recommended size; deployed to /public
          image: 'https://www.bookmycoaching.com/logo_256x256.png',
          prefill: {
            email: userEmail,
          },
          theme: {
            color: themeColor,
            backdrop_color: 'rgba(15, 23, 42, 0.7)',
          },
          handler: async (response: {
            razorpay_order_id: string;
            razorpay_payment_id: string;
            razorpay_signature: string;
          }) => {
            try {
              const status = await verifyPurchasePayment(token, order.orderId, {
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
              resolve(status);
            } catch (err) {
              reject(err instanceof Error ? err : new Error('Payment verification failed'));
            }
          },
          modal: {
            ondismiss: () => reject(new Error('Checkout closed before payment completed')),
          },
        });
        razorpay.open();
      });
    },
    onSuccess: async () => {
      if (onRefresh) await onRefresh();
      toast.success('BMC Coins added to your account');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Payment failed');
    },
  });
}
